import type {
  BespokeTemplateResponse,
  FormSchemaVersion,
} from '../../services/api/types.gen';
import type { RogerDocFormType } from '../../services/api/models/RogerDocFormType';

/** Identifies a FormSchemaVersion: form_type + name + version. */
export type FormSchemaVersionPin = {
  form_type: RogerDocFormType;
  schema_version_name: string;
  form_schema_version: string;
};

/** Stable key for set membership / export allowlists. */
export function formSchemaVersionPinKey(pin: FormSchemaVersionPin): string {
  return `${pin.form_type}\0${pin.schema_version_name}\0${pin.form_schema_version}`;
}

/** Pin key from a bespoke template API row. */
export function formSchemaVersionPinKeyFromTemplate(
  template: Pick<
    BespokeTemplateResponse,
    'form_type' | 'schema_version_name' | 'form_schema_version'
  >
): string {
  return `${template.form_type}\0${template.schema_version_name}\0${template.form_schema_version}`;
}

/**
 * Wire pin from a bespoke template row.
 * `form_type` is a plain string on the API response; cast to the RogerDoc union.
 */
export function pinFromBespokeTemplate(
  template: Pick<
    BespokeTemplateResponse,
    'form_type' | 'schema_version_name' | 'form_schema_version'
  >
): FormSchemaVersionPin {
  return {
    form_type: template.form_type as RogerDocFormType,
    schema_version_name: template.schema_version_name,
    form_schema_version: template.form_schema_version,
  };
}

/** Human-readable pin for unresolved-destination messages. */
export function formSchemaVersionPinLabel(pin: FormSchemaVersionPin): string {
  return `${pin.form_type} · ${pin.schema_version_name} (${pin.form_schema_version})`;
}

/** Build a wire pin from a loaded `FormSchemaVersion` + its form type. */
export function pinFromFormSchemaVersion(
  formType: RogerDocFormType,
  version: FormSchemaVersion
): FormSchemaVersionPin | null {
  const form_schema_version = version.version?.trim();
  if (!form_schema_version) return null;
  return {
    form_type: formType,
    schema_version_name: version.name,
    form_schema_version,
  };
}

/** All wire pins for the versions listed for one form type. */
export function pinsFromFormSchemaVersions(
  formType: RogerDocFormType,
  versions: FormSchemaVersion[]
): FormSchemaVersionPin[] {
  return versions
    .map(version => pinFromFormSchemaVersion(formType, version))
    .filter((pin): pin is FormSchemaVersionPin => pin != null);
}

export function bespokeTemplateMatchesPin(
  template: BespokeTemplateResponse,
  pin: FormSchemaVersionPin
): boolean {
  return (
    template.form_type === pin.form_type &&
    template.schema_version_name.trim() === pin.schema_version_name.trim() &&
    template.form_schema_version === pin.form_schema_version
  );
}

/** Bespoke rows for one schema version pin. */
export function filterBespokeTemplatesForPin(
  templates: readonly BespokeTemplateResponse[],
  pin: FormSchemaVersionPin
): BespokeTemplateResponse[] {
  return templates.filter(
    t => t.section_name && bespokeTemplateMatchesPin(t, pin)
  );
}

/** The one layout per section for a pin (rows without layout JSON skipped). */
export function templatesBySectionForPin(
  templates: readonly BespokeTemplateResponse[],
  pin: FormSchemaVersionPin
): Map<string, BespokeTemplateResponse> {
  const map = new Map<string, BespokeTemplateResponse>();
  for (const template of templates) {
    if (
      !bespokeTemplateMatchesPin(template, pin) ||
      !template.section_name ||
      !template.layout_json
    ) {
      continue;
    }
    map.set(template.section_name, template);
  }
  return map;
}
